import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ToastrService } from 'ngx-toastr';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { TemplateConfig } from 'src/app/models/template-config';
import { TemplateConfigService } from '../../services/template-config/template-config.service';
import * as templateConfigActions from './template-config.actions';

@Injectable()
export class TemplateConfigEffects {
  getTemplateConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateConfigActions.getTemplateConfiguration),
      switchMap(({ studyId, templateId, profileId }) => {
        return this.templateConfigurationService.getTemplateConfiguration(studyId, templateId, profileId).pipe(
          map((config: TemplateConfig) => {
            const configCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(config);
            return templateConfigActions.getTemplateConfigurationSuccess({ templateConfig: configCamelCase });
          }),
          catchError((err: HttpErrorResponse) => of(templateConfigActions.getTemplateConfigurationError({ err })))
        );
      })
    )
  );

  getTemplateConfigurationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateConfigActions.getTemplateConfigurationError),
        tap(({ err }) => {
          this.toastrService.error('Unable to load configuration');
        })
      ),
    { dispatch: false }
  );

  saveTemplateConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateConfigActions.saveTemplateConfiguration),
      switchMap(({ config, studyId }) => {
        const configSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(config);
        return this.templateConfigurationService.saveTemplateConfiguration(configSnakeCase, studyId).pipe(
          map(() => {
            return templateConfigActions.saveTemplateConfigurationSuccess();
          }),
          catchError((err: HttpErrorResponse) => of(templateConfigActions.saveTemplateConfigurationError({ err })))
        );
      })
    )
  );

  saveTemplateConfigurationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateConfigActions.saveTemplateConfigurationSuccess),
        tap(() => {
          this.toastrService.success('Configuration saved!');
        })
      ),
    { dispatch: false }
  );

  saveTemplateConfigurationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateConfigActions.saveTemplateConfigurationError),
        tap(({ err }) => {
          this.toastrService.error('Unable to save configuration');
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private templateConfigurationService: TemplateConfigService,
    private toastrService: ToastrService
  ) {}
}
